import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../../components/Button';
import { DashboardLayout } from '../../../components/dashboard/DashboardLayout';
import { qrApi } from '../../../api/services/qr.service';
import { QRCodeHeader } from './components/QRCodeHeader';
import { StatsGrid } from './components/StatsGrid';
import { AnalyticsCharts } from './components/AnalyticsCharts';
import { RoutingRulesSection } from './components/RoutingRulesSection';
import { AnalyticsSummary, QRCodeData } from './types';

const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3, ease: 'easeOut' }
  }
};

export function QRCodeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qrIdentifier = id || '';

  // Fetch QR code details
  const { data: qrCodeData, isLoading: isLoadingQR } = useQuery({
    queryKey: ['qr-code', qrIdentifier],
    // @ts-ignore - API accepts both string and number
    queryFn: () => qrApi.getQRCode(qrIdentifier),
    enabled: !!qrIdentifier
  });

  // Cast to QRCodeData for type safety
  const qrCode = qrCodeData as QRCodeData | undefined;

  // Fetch analytics summary
  const { data: analyticsData, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['qr-analytics-summary', qrIdentifier],
    // @ts-ignore - API accepts both string and number
    queryFn: () => qrApi.getAnalyticsSummary(qrIdentifier),
    enabled: !!qrIdentifier,
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: 2
  });

  // Cast to AnalyticsSummary for type safety
  const analytics = analyticsData as AnalyticsSummary | undefined;

  // Loading state
  if (isLoadingQR || isLoadingAnalytics) {
    return (
      <DashboardLayout currentPage="qr-codes">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Not found state
  if (!qrCode) {
    return (
      <DashboardLayout currentPage="qr-codes">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <p className="text-gray-600 mb-4">QR code not found</p>
          <Button onClick={() => navigate('/dashboard/qr-codes')}>
            Back to QR Codes
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="qr-codes">
      <motion.div
        variants={pageVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto space-y-6"
      >
        {/* Header with title, badges, and action buttons */}
        <QRCodeHeader
          qrCode={qrCode}
          analytics={analytics}
          onEditClick={() => navigate(`/dashboard/qr-codes/${qrIdentifier}/edit`)}
        />

        {/* Stats Overview */}
        <StatsGrid analytics={analytics} isLoading={isLoadingAnalytics} />

        {/* Charts Grid */}
        <AnalyticsCharts
          analytics={analytics}
          isLoading={isLoadingAnalytics}
        />

        {/* Routing Rules */}
        <RoutingRulesSection qrCode={qrCode} />
      </motion.div>
    </DashboardLayout>
  );
}

// Default export for backwards compatibility
export default QRCodeDetailPage;
